import { cn } from "@/lib/utils";
import { FeaturePrd, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { Project } from "@/utils/types";
import {
  format,
  formatDistanceStrict,
  isWithinInterval,
  subDays,
  startOfDay,
  isPast,
  addDays,
  isSameDay,
} from "date-fns";
import {
  PlusIcon,
  Loader2,
  UserPlus,
  ArrowUpDown,
  Calendar,
  Tag,
  Clock,
  BarChart,
  CircleDashed,
  UserIcon,
  UserCircle,
  Minus,
  User,
  Circle,
  TriangleDashed,
} from "lucide-react";
import * as React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { useCreateCanvasModal } from "@/hooks/useCreateCanvasModal";
import { CanvasContextMenu } from "./CanvasContextMenu";
import CreateCanvasModal from "../CreateCanvasModal";
import { FilterState, ProjectLabel } from "./types";
import { TEMPLATE_PROJECT_PATHS } from "@/utils/templateProjects";
import NoRepositoryModal from "../NoRepositoryModal";
import CreateRepositoryModal from "../CreateRepositoryModal";
import { GitProvider } from "@/utils/gitProviderUtils";
import { useKanbanActions } from "@/hooks/useKanbanActions";
import { ADMIN_USER_IDS, useUserId, mapCanvasToIssue } from "@/utils/utils.tsx";
import { IssueGrid } from "@/components/common/issues/issue-grid";
import { CirclePartialFillIcon } from "@/assets/icons/circle-partial-fill-icon";
import { CircleFillCheckmarkIcon } from "@/assets/icons/circle-fill-checkmark-icon";
import { CircleFillXIcon } from "@/assets/icons/circle-fill-x-icon";
import { IssueList } from "@/components/common/issues/issue-list";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setCanvasPreviews } from "@/store/supabaseSlice";
import { useFocusStore } from "@/store/zustand/focusStore";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useIssueKeyboardShortcuts } from "@/hooks/useIssueKeyboardShortcuts";
import {
  CommandPaletteMode,
  CommandPaletteOption,
  CommandPaletteSubMenuItem,
  SubMenuApplicationStatus,
} from "@/store/zustand/commandPaletteStore";
import { OptionCategory, useCommandPalette } from "@/hooks/useCommandPalette";
import HalfFilledTriangleIcon from "@/assets/icons/half-filled-triangle-icon";
import { useIssuesStore } from "@/store/issues-store";
import { defaultPriorities } from "@/types/priorities";
import { ESTIMATE_POINTS, getTShirtSize, Issue } from "@/types/issues";
import { LabelInterface } from "@/types/labels";
import { useShortcuts } from "../providers/ShortcutProvider";
import { useUser } from "@clerk/clerk-react";
import { getModifierKey } from "@/utils/utils";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import { Button } from "../ui/button";
// TODO: Use local status icon definitions to avoid React version conflicts
// import { status as STATUS_LIST } from '@/mock-data/status';

export const STATUS_LIST = [
  {
    id: "backlog",
    name: "Backlog",
    color: "#ec4899",
    icon: () => <CircleDashed className="size-4 pl-[3px]" color="#F5F5F6" />,
    headerIcon: () => (
      <CircleDashed className="size-4 pl-[3px]" color="#F5F5F6" />
    ),
    badgeIcon: () => <CircleDashed className="w-3.5 h-3.5" color="#F5F5F6" />,
    filterIcon: () => <CircleDashed className="size-3" color="#F5F5F6" />,
  },
  {
    id: "to-do",
    name: "Todo",
    color: "#f97316",
    icon: () => <Circle className="size-4 pl-[3px]" color="#F5F5F6" />,
    headerIcon: () => <Circle className="size-4 pl-[3px]" color="#F5F5F6" />,
    badgeIcon: () => <Circle className="w-3.5 h-3.5" color="#F5F5F6" />,
    filterIcon: () => <Circle className="size-3" color="#F5F5F6" />,
  },
  {
    id: "in-progress",
    name: "In Progress",
    color: "#facc15",
    icon: () => <CirclePartialFillIcon color="#CA8A04" />,
    headerIcon: () => <CirclePartialFillIcon color="#CA8A04" />,
  },
  {
    id: "in-review",
    name: "In Review",
    color: "#f97316",
    icon: () => (
      <CirclePartialFillIcon color="#16A34A" startAngle={270} endAngle={90} />
    ),
    headerIcon: () => (
      <CirclePartialFillIcon color="#16A34A" startAngle={270} endAngle={90} />
    ),
  },
  {
    id: "blocked",
    name: "Blocked",
    color: "#f97316",
    icon: () => (
      <CirclePartialFillIcon color="#DC2626" startAngle={180} endAngle={90} />
    ),
    headerIcon: () => (
      <CirclePartialFillIcon color="#DC2626" startAngle={180} endAngle={90} />
    ),
  },
  {
    id: "done",
    name: "Done",
    color: "#22c55e",
    icon: () => <CircleFillCheckmarkIcon color="#6448EF" />,
    headerIcon: () => <CircleFillCheckmarkIcon color="#6448EF" />,
  },
  {
    id: "canceled",
    name: "Canceled",
    color: "#f97316",
    icon: () => <CircleFillXIcon circleColor="#606064" />,
    headerIcon: () => <CircleFillXIcon circleColor="#606064" />,
  },
  {
    id: "paused",
    name: "Paused",
    color: "#0ea5e9",
    icon: () => (
      <span className="inline-block w-3 h-3 rounded-full bg-blue-400" />
    ),
  },
  {
    id: "technical-review",
    name: "Technical Review",
    color: "#22c55e",
    icon: () => (
      <span className="inline-block w-3 h-3 rounded-full bg-green-400" />
    ),
  },
  {
    id: "completed",
    name: "Completed",
    color: "#8b5cf6",
    icon: () => (
      <span className="inline-block w-3 h-3 rounded-full bg-purple-400" />
    ),
  },
];

export enum KanbanDefaultStates {
  BACKLOG = "Backlog",
  TO_DO = "Todo",
  IN_PROGRESS = "In Progress",
  IN_REVIEW = "In Review",
  BLOCKED = "Blocked",
  DONE = "Done",
  CANCELED = "Canceled",
}

export const STATE_KEY_TO_STATUS_ID: Record<string, string> = {
  BACKLOG: "backlog",
  TO_DO: "to-do",
  IN_PROGRESS: "in-progress",
  IN_REVIEW: "in-review",
  BLOCKED: "blocked",
  DONE: "done",
  CANCELED: "canceled",
};

// Filter STATUS_LIST to only include statuses used in columns
export const columnStatuses = STATUS_LIST.filter((status) =>
  Object.values(STATE_KEY_TO_STATUS_ID).includes(status.id)
);

interface ColumnProps {
  stateKey: string;
  title: string;
  canvases: any[];
  onSelect: (project: any, canvas: any) => void;
  project: Project | null;
  canvasIdToPrd: Record<string, FeaturePrd>;
  updateCanvasLabels: (canvas: any, newLabels: Array<string>) => Promise<void>;
  updateProjectLabels: (
    newLabel: Omit<ProjectLabel, "id">,
    canvas?: any
  ) => Promise<void>;
  updateCanvasAssignedUserId: (
    canvas: any,
    assignedUserId: string | null
  ) => Promise<void>;
  onClickAdd: (stateKey: string) => void;
  isDragging: boolean;
  showCreateRepo: boolean;
  issueRefs: { [key: string]: HTMLDivElement };
  visibleIssueIds: string[];
}

const KanbanColumn: React.FC<ColumnProps> = React.memo(
  ({
    stateKey,
    title,
    canvases,
    onSelect,
    project,
    canvasIdToPrd,
    onClickAdd,
    isDragging,
    showCreateRepo,
    updateCanvasAssignedUserId,
    issueRefs,
    visibleIssueIds,
  }) => {
    // Map stateKey to statusId for icon lookup
    const statusId = STATE_KEY_TO_STATUS_ID[stateKey] || "backlog";
    const statusObj = STATUS_LIST.find((s) => s.id === statusId);
    const StatusIcon = statusObj?.icon;
    const StatusHeaderIcon = statusObj?.headerIcon;

    if (!project) {
      // render skeleton
      return (
        <div className="flex flex-col basis-[20.65rem] gap-4 shrink-0">
          <div className="text-2xs font-semibold">{title}</div>
          <div className="flex flex-col gap-2 max-w-full grow pb-8 group rounded-md overflow-y-scroll min-h-0">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-full col-span-1 bg-secondary-n-950 rounded-lg p-3 flex flex-col min-h-28 justify-between animate-pulse"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row items-center justify-between">
                    <div className="h-4 w-24 bg-secondary-n-900 rounded" />
                    <div className="h-6 w-6 bg-secondary-n-900 rounded-full" />
                  </div>
                  <div className="grow min-h-8">
                    <div className="h-4 w-3/4 bg-secondary-n-900 rounded" />
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between gap-1">
                  <div className="h-4 w-16 bg-secondary-n-900 rounded" />
                  <div className="h-4 w-20 bg-secondary-n-900 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col basis-[17rem] min-h-[calc(100vh-120px)] shrink-0 bg-sidebar/50 rounded-xl overflow-hidden shadow-sm">
        <div
          className={`text-xs font-normal p-2 tracking-wide flex items-center flex-shrink-0`}
        >
          <div className="flex flex-row gap-1 items-center">
            {/* Status Icon on the left */}
            {StatusHeaderIcon ? <StatusHeaderIcon /> : null}
            {/* Title and count badge */}
            <span className="caption text-2xs font-medium flex items-center">
              {title}
            </span>
            <span className="px-1 rounded-xs text-2xs font-medium bg-secondary-n-900 text-center flex items-center justify-center">
              {canvases.length}
            </span>
          </div>
          {/* Group the action buttons together */}
          <div className="ml-auto flex items-center gap-1">
            {/* Add Feature button */}
            <Button
              onClick={() => onClickAdd(stateKey)}
              className="w-7 h-7 rounded-md"
              aria-label="Add Feature"
              type="button"
              variant="ghost"
              size="icon"
            >
              <PlusIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Droppable droppableId={stateKey} ignoreContainerClipping={true}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={cn(
                "flex flex-col gap-2 max-w-full grow overflow-y-auto p-2 group",
                snapshot.isDraggingOver && "bg-secondary-n-950"
              )}
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgb(82 82 91) transparent",
                height: "calc(100vh - 180px)",
              }}
            >
              <div
                className={cn("flex flex-col gap-2", {
                  hidden: !canvases.length,
                })}
              >
                {canvases.map((canvas, index) => {
                  const updatedAt = new Date(canvas.last_updated);
                  const featurePrd = canvasIdToPrd[canvas.id];
                  const now = new Date();
                  const threeDaysAgo = subDays(now, 5);

                  let dateString = null;

                  if (
                    isWithinInterval(updatedAt, {
                      start: threeDaysAgo,
                      end: now,
                    })
                  ) {
                    // @ts-expect-error TS2322
                    dateString = formatDistanceStrict(updatedAt, now, {
                      addSuffix: true,
                    });
                  } else {
                    // @ts-expect-error TS2322
                    dateString = format(updatedAt, "PPP");
                  }

                  const issue = mapCanvasToIssue(
                    canvas,
                    featurePrd,
                    Array.isArray(project?.labels) ? project.labels : [],
                    Array.isArray(project?.users) ? project.users : []
                  );

                  return (
                    <Draggable
                      draggableId={canvas.id}
                      index={index}
                      key={canvas.id}
                    >
                      {(provided, snapshot) => (
                        <div>
                          <IssueGrid
                            icon={StatusIcon}
                            issue={issue}
                            users={
                              Array.isArray(project?.users) ? project.users : []
                            }
                            labels={
                              Array.isArray(project?.labels)
                                ? project.labels
                                : []
                            }
                            statuses={columnStatuses}
                            onAssignUser={(_issueId, userId) =>
                              updateCanvasAssignedUserId(canvas, userId)
                            }
                            onClick={() => onSelect(project, canvas)}
                            provided={provided}
                            snapshot={snapshot}
                            issueRefs={issueRefs}
                            visibleIssueIds={visibleIssueIds}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
              </div>
              <Button
                onClick={() => onClickAdd(stateKey)}
                aria-label="Add Canvas"
                type="button"
                variant="outline"
                size="sm"
                className="hover:bg-secondary-n-950 transition-colors opacity-0 group-hover:opacity-100 ring-1 ring-inset ring-secondary-n-800"
              >
                <PlusIcon className="w-4 h-4" />
              </Button>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  }
);

interface Props {
  project: Project | null;
  onSelect: (project: any, canvas: any) => void;
  filters: FilterState;
  viewType: "grid" | "list";
}

const CanvasKanban: React.FC<Props> = ({
  onSelect,
  project,
  filters,
  viewType,
}) => {
  const userId = useUserId();

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const { supabase, setSupabase, forceInitalize } = useIssuesStore();

  const featurePrdsStatus = useSupabaseStore(
    (state) => state.featurePrdsStatus
  );

  const { triggerRefetchCanvasPreviews } = useSupabase();

  useEffect(() => {
    if (project) {
      forceInitalize();
    }
  }, [triggerRefetchCanvasPreviews]);

  // Initialize issues store with canvas previews
  useEffect(() => {
    if (!supabase && supabaseClient) {
      setSupabase(supabaseClient);
    }
    if (
      supabase &&
      project &&
      featurePrdsStatus === "succeeded" &&
      useIssuesStore.getState().currentProject !== (project as any)
    ) {
      useIssuesStore.setState({
        currentProject: project as any,
        supabase: supabase,
      });
      forceInitalize();
    }
  }, [project, featurePrdsStatus, supabase]);
  const dispatch = useDispatch<any>();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get("project_id");
  const { registerShortcut, unregisterShortcut } = useShortcuts();

  const {
    setFocusedIssue,
    setLastFocusedIssue,
    clearFocus,
    focusedIssueId,
    lastFocusedIssueId,
  } = useFocusStore();

  // Clear focus states when project changes - moved this earlier
  useEffect(() => {
    setFocusedIssue(null);
    setLastFocusedIssue(null);
  }, [selectedProjectId]);

  const [showNoRepoModal, setShowNoRepoModal] = React.useState(false);
  const [showCreateRepoModal, setShowCreateRepoModal] = React.useState(false);
  const [originatingGitProvider, setOriginatingGitProvider] =
    React.useState<GitProvider>(GitProvider.GITHUB);
  const { isOpen, setIsOpen } = useCreateCanvasModal();

  // Determine if project has a repo
  const hasRepo = React.useMemo(() => {
    if (project && typeof project.repoPath === "string") {
      const repoPath = project.repoPath.toLowerCase();
      // Check if project has a real repository (not a template)
      const isTemplateProject = TEMPLATE_PROJECT_PATHS.some(
        (templatePath) => templatePath === repoPath
      );

      return !isTemplateProject;
    }
    return false;
  }, [project]);

  const featurePrds = useSupabaseStore((store) => store.featurePrds);

  const canvasPreviews = useIssuesStore((store) => store.canvasPreviews);

  // Add loading state check
  const isLoading = React.useMemo(() => {
    if (!selectedProjectId) return false;

    // Check if we have the project data
    if (!project) return true;

    return false;
  }, [selectedProjectId, project]);

  const canvasIdToPrd = React.useMemo(() => {
    const prdMap: { [canvasId: string]: FeaturePrd } = {};
    Object.keys(featurePrds).forEach((prdId) => {
      const prd = featurePrds[prdId];
      prdMap[prd.canvas_id] = prd;
    });
    return prdMap;
  }, [featurePrds]);

  // Filter for the canvases
  const canvasesToShow = React.useMemo(() => {
    if (!selectedProjectId) return [];
    if (!project?.users) return [];

    return Object.values(canvasPreviews).filter((canvas) => {
      const projectIdMatch = canvas.project_id === selectedProjectId;
      const prd = canvasIdToPrd[canvas.id];
      const searchMatch = filters?.search
        ? (prd?.title || canvas.current_branch || project?.name)
            ?.toLowerCase()
            ?.includes(filters?.search.toLowerCase())
        : true;
      const userIdMatch = (() => {
        if (!filters?.userIds.length) return true;

        return filters.userIds.some((userId) => {
          if (userId === "none") {
            return !canvas.assigned_user_id;
          }
          return canvas.assigned_user_id === userId;
        });
      })();
      const labelsMatch = (() => {
        if (!filters?.labels.length) return true;

        return filters.labels.some((filterLabel) => {
          if (filterLabel === "none") {
            return !canvas?.labels?.length;
          }
          return canvas?.labels?.includes(filterLabel);
        });
      })();
      const statusMatch =
        !filters?.statuses?.length ||
        filters?.statuses.includes(
          prd?.status
            ? STATE_KEY_TO_STATUS_ID[
                Object.keys(KanbanDefaultStates).find(
                  (key) => KanbanDefaultStates[key] === prd.status
                ) || "BACKLOG"
              ] || "backlog"
            : "backlog"
        );
      const estimateMatch = (() => {
        if (!filters?.estimates?.length) return true;
        // @ts-expect-error TS18047
        const prdEstimate = prd?.estimate;
        return (
          (filters.estimates.includes(0) && !prdEstimate) ||
          filters.estimates.includes(prdEstimate || 0)
        );
      })();
      const priorityMatch = (() => {
        if (!filters?.priorities?.length) return true;
        return filters.priorities.includes(prd?.priority || 0);
      })();
      const dueDateMatch = (() => {
        if (!filters?.dueDates) return true;
        // @ts-expect-error TS18047
        const dueDate = prd?.due_date ? new Date(prd.due_date) : null;

        // If no filters are selected, show all items
        if (
          !filters.dueDates.none &&
          !filters.dueDates.overdue &&
          !filters.dueDates.today &&
          !filters.dueDates.thisWeek &&
          !filters.dueDates.nextWeek &&
          !filters.dueDates.later
        ) {
          return true;
        }

        // If the item has no due date, only show it if "none" filter is selected
        if (!dueDate) {
          return filters.dueDates.none;
        }

        const today = startOfDay(new Date());

        // Check each selected filter with OR logic
        return (
          // Overdue
          (filters.dueDates.overdue &&
            isPast(dueDate) &&
            !isSameDay(dueDate, today)) ||
          // Today
          (filters.dueDates.today && isSameDay(dueDate, today)) ||
          // This week
          (filters.dueDates.thisWeek &&
            isWithinInterval(dueDate, {
              start: today,
              end: addDays(today, 7),
            })) ||
          // Next week
          (filters.dueDates.nextWeek &&
            isWithinInterval(dueDate, {
              start: addDays(today, 7),
              end: addDays(today, 14),
            })) ||
          // Later
          (filters.dueDates.later && dueDate > addDays(today, 14))
        );
      })();
      const runningBaseAndNotMain =
        userId &&
        ADMIN_USER_IDS.includes(userId) &&
        project?.name?.toLowerCase().includes("running base") &&
        canvas.current_branch !== "MAIN" &&
        !prd?.title?.startsWith("MAIN");

      return (
        projectIdMatch &&
        searchMatch &&
        userIdMatch &&
        labelsMatch &&
        statusMatch &&
        estimateMatch &&
        priorityMatch &&
        dueDateMatch &&
        !runningBaseAndNotMain
      );
    });
  }, [
    canvasPreviews,
    selectedProjectId,
    filters,
    canvasIdToPrd,
    project,
    project?.users,
    userId,
  ]);

  // Extract sorting function for reuse
  const sortColumnIssues = useCallback((issues: any[], canvasIdToPrd: any) => {
    return [...issues].sort((a, b) => {
      const prdA = canvasIdToPrd[a.id];
      const prdB = canvasIdToPrd[b.id];

      // If both have orders, use them
      if (prdA?.order !== undefined && prdB?.order !== undefined) {
        return prdA.order - prdB.order;
      }

      // If only one has order, prioritize the ordered one
      if (prdA?.order !== undefined) return -1;
      if (prdB?.order !== undefined) return 1;

      // Neither has order, sort by last_updated (most recent first)
      const timeA = a.last_updated || "-1";
      const timeB = b.last_updated || "-1";
      return timeB.localeCompare(timeA);
    });
  }, []);

  // Group canvases into columns
  const stateToCanvases = React.useMemo(() => {
    // Initialize columns with empty canvas arrays
    const stateToCanvases = {};
    Object.keys(KanbanDefaultStates).forEach((key) => {
      stateToCanvases[KanbanDefaultStates[key]] = {
        stateKey: key,
        title: STATUS_LIST.find((s) => s.id === STATE_KEY_TO_STATUS_ID[key])
          ?.name,
        canvases: [],
      };
    });

    // First, group canvases into their respective columns
    canvasesToShow.forEach((canvas) => {
      const prd = canvasIdToPrd[canvas.id];
      let status = prd?.status || KanbanDefaultStates.BACKLOG;

      // If the status is invalid, set it to backlog
      if (!stateToCanvases[status]) {
        status = KanbanDefaultStates.BACKLOG;
        prd.status = status;
      }

      stateToCanvases[status].canvases.push(canvas);
    });

    // Then sort each column's canvases
    Object.keys(stateToCanvases).forEach((columnKey) => {
      const column = stateToCanvases[columnKey];
      column.canvases = sortColumnIssues(column.canvases, canvasIdToPrd);
    });

    return stateToCanvases;
  }, [canvasesToShow, canvasIdToPrd, sortColumnIssues]);

  const [isDragging, setIsDragging] = React.useState(false);

  const onDragEnd = React.useCallback(
    async (result) => {
      setIsDragging(false);
      const { destination, source, draggableId } = result;

      if (!destination || !supabaseClient) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      )
        return;

      const canvasChangedStatus =
        source.droppableId !== destination.droppableId;
      const stateKey = destination.droppableId;
      const canvasDropping = canvasPreviews[draggableId];
      const desiredCanvasIndex = destination.index;

      let newCanvasList =
        stateToCanvases[KanbanDefaultStates[stateKey]].canvases;

      if (!canvasChangedStatus) {
        newCanvasList = newCanvasList.filter(
          (canvas) => canvas.id !== canvasDropping.id
        );
      }

      newCanvasList.splice(desiredCanvasIndex, 0, canvasDropping);

      // Go through the canvases, and update the status and/or order if needed
      const prdsToUpdate: any[] = [];
      const prdsToCreate: any[] = [];
      newCanvasList.forEach((canvas, index) => {
        const prd = canvasIdToPrd[canvas.id];
        if (!prd) {
          prdsToCreate.push({
            id: uuidv4().toString(),
            canvas_id: canvas.id,
            status: KanbanDefaultStates[stateKey],
            order: index,
          });
        } else if (
          prd.order !== index ||
          (canvas.id === canvasDropping.id && canvasChangedStatus)
        ) {
          prdsToUpdate.push({
            ...prd,
            order: index,
            status: KanbanDefaultStates[stateKey],
          });
        }
      });

      const allPrds = [...prdsToCreate, ...prdsToUpdate];
      if (allPrds.length === 0) return;

      // Update the values both in the local state and in the DB
      const sbStoreState = useSupabaseStore.getState();

      sbStoreState.onDbSnapshot("featurePrds", {
        newItems: allPrds,
        deletedItems: [],
      });

      // Now, update supabase
      try {
        await supabaseClient
          .from("feature_prd")
          .upsert(allPrds, { onConflict: "canvas_id" });

        useIssuesStore.getState().featurePrds = {
          ...useIssuesStore.getState().featurePrds,
          ...Object.fromEntries(allPrds.map((prd) => [prd.id, prd])),
        };
      } catch (error) {
        console.error("Error updating PRDs:", error);
      }
      clearSelection();
    },
    [canvasIdToPrd, stateToCanvases, canvasPreviews, supabaseClient]
  );

  const onDragStart = () => {
    setIsDragging(true);
    clearSelection();
  };

  const updateCanvasLabels = React.useCallback(
    async (canvas: any, newLabels: Array<string>) => {
      if (!supabaseClient) return;

      const newCanvas = {
        ...canvas,
        labels: newLabels,
        last_updated: new Date().toISOString(),
      };

      // update the local store
      dispatch(setCanvasPreviews([newCanvas]));

      await supabaseClient
        .from("canvases")
        .update({ labels: newLabels })
        .eq("id", newCanvas.id);
    },
    [supabaseClient, dispatch]
  );

  const updateProjectLabels = React.useCallback(
    async (newLabel: Omit<ProjectLabel, "id">, canvas?: any) => {
      if (!supabaseClient || !project) return;

      const updatedLabel = {
        ...newLabel,
        id: uuidv4().toString(),
      };

      await supabaseClient
        .from("projects")
        .update({
          labels: [...((project.labels as any) || []), updatedLabel],
        })
        .eq("id", project.id);

      if (canvas) {
        const updatedLabelsForCanvas = [
          ...(canvas.labels || []),
          updatedLabel.id,
        ];
        await updateCanvasLabels(canvas, updatedLabelsForCanvas);
      }
    },
    [supabaseClient, project, updateCanvasLabels]
  );

  const updateCanvasAssignedUserId = React.useCallback(
    async (canvas: any, assignedUserId: string | null) => {
      if (!supabaseClient) return;

      // Check if we need to create a PRD first
      let prd = canvasIdToPrd[canvas.id];
      if (!prd) {
        const newPrd = {
          id: uuidv4().toString(),
          canvas_id: canvas.id,
          status: KanbanDefaultStates.BACKLOG,
          order: 0,
          created_at: new Date().toISOString(),
          canvas_visual_context: undefined,
          design_suggestions: null,
          mermaid_diagram: null,
          prd: null,
          priority: null,
          title: null,
        };

        // Create in Supabase
        try {
          const { data, error } = await supabaseClient
            .from("feature_prd")
            .insert(newPrd)
            .select()
            .single();

          if (error) throw error;

          // Update Zustand store
          const sbStore = useSupabaseStore.getState();
          sbStore.onDbSnapshot("featurePrds", {
            newItems: [newPrd],
            deletedItems: [],
          });

          // Update our local reference
          prd = newPrd;
        } catch (error) {
          console.error("Error creating PRD:", error);
          return;
        }
      }

      const newCanvas = {
        ...canvas,
        assigned_user_id: assignedUserId,
        last_updated: new Date().toISOString(),
      };

      // Update local store
      dispatch(setCanvasPreviews([newCanvas]));

      // Update Supabase
      await supabaseClient
        .from("canvases")
        .update({ assigned_user_id: assignedUserId })
        .eq("id", newCanvas.id);
    },
    [supabaseClient, dispatch, canvasIdToPrd]
  );

  const handleCreateRepository = (provider: "github" | "gitlab") => {
    let gitProvider: GitProvider = GitProvider.GITHUB;
    if (provider === "github") {
      gitProvider = GitProvider.GITHUB;
    } else if (provider === "gitlab") {
      gitProvider = GitProvider.GITLAB;
    }
    setOriginatingGitProvider(gitProvider);
    setShowNoRepoModal(false);
    setShowCreateRepoModal(true);
  };

  const handleButtonClick = useCallback(
    (key: string) => {
      if (hasRepo || canvasesToShow.length !== 1) {
        // If repo exists, trigger create canvas modal
        setIsOpen(key);
        useProjectModalStore.getState().addProjectModal("create-canvas-modal");
      } else {
        // If no repo and canvases.length is 1, show repository connection modal
        setShowNoRepoModal(true);
      }
    },
    [hasRepo, canvasesToShow, setIsOpen]
  );

  const {
    selectedIssueIds,
    clearSelection,
    toggleIssueSelection,
    selectAllVisible,
    selectAllInColumn,
    lastSelectedIssueId,
  } = useIssueSelectionStore();

  const {
    bulkUpdateIssueAssignee,
    bulkUpdateIssuePriority,
    bulkUpdateIssueStatus,
    bulkUpdateIssueEstimate,
    bulkUpdateIssueLabel,
    bulkUpdateIssueDueDate,
  } = useIssuesStore();

  function assignUserToIssues(userId: string | null) {
    let user = (project?.users as any[])?.find((u) => u.id === userId);
    if (!user) {
      user = null;
    }

    if (selectedIssueIds.length === 0) {
      return;
    }
    bulkUpdateIssueAssignee(selectedIssueIds, user);
  }

  function changePriorityOfIssues(priority: string | null) {
    let priorityId: number | null = -1;
    if (priority) {
      priorityId = parseInt(priority, 10);
    }

    if (priorityId === -1) {
      priorityId = null;
    }

    if (selectedIssueIds.length === 0) {
      return;
    }

    bulkUpdateIssuePriority(selectedIssueIds, priorityId);
  }

  const changeStatusOfIssues = (statusId: string | null) => {
    if (!statusId) {
      return;
    }

    if (selectedIssueIds.length === 0) {
      return;
    }

    bulkUpdateIssueStatus(selectedIssueIds, statusId);
  };

  const setDueDateOfIssues = (date: string | null) => {
    if (selectedIssueIds.length === 0) {
      return;
    }

    bulkUpdateIssueDueDate(selectedIssueIds, date);
  };

  const setLabelOfIssues = (labelId: string | null) => {
    if (selectedIssueIds.length === 0) {
      return;
    }

    let labelInterface: LabelInterface | null = null;
    if (labelId === null) {
      labelInterface = null;
    } else {
      const label = (project?.labels as any[])?.find((l) => l.id === labelId);

      if (label) {
        labelInterface = {
          id: label.id,
          label: label.name,
          color: label.color,
          name: label.name,
        };
      }
    }

    // now decide if we need to add or remove the label
    // get the canvases for each selected issue
    const issues = useIssuesStore
      .getState()
      .getAllIssues()
      .filter((issue) => selectedIssueIds.includes(issue.id));
    const issueLabels = issues.map((issue) => {
      const labels = issue.labels;
      if (Array.isArray(labels)) {
        return {
          issueId: issue.id,
          labels: labels as unknown as string[],
        };
      } else {
        return {
          issueId: issue.id,
          labels: [],
        };
      }
    });

    if (labelInterface === null) {
      // set all issues to have no label
      issueLabels.forEach((entry) => {
        entry.labels = [];
      });

      const issueIdToLabelInterfaces: {
        issueId: string;
        labels: LabelInterface[];
      }[] = [];
      issueLabels.forEach((entry) => {
        issueIdToLabelInterfaces.push({ issueId: entry.issueId, labels: [] });
      });

      bulkUpdateIssueLabel(issueIdToLabelInterfaces);
      return;
    }

    // go through each entry in the issueLabels array
    let allIssuesHaveLabel = true;
    issueLabels.forEach((entry) => {
      const labels = entry.labels;
      let hasLabel = false;

      if (labelId === null && labels.length === 0) {
        hasLabel = true;
      } else {
        labels.forEach((label) => {
          if (label === labelId) {
            hasLabel = true;
          }
        });
      }

      if (!hasLabel) {
        allIssuesHaveLabel = false;
      }
    });

    let modifiedIssueLabels: { issueId: string; labels: string[] }[] = [];

    if (allIssuesHaveLabel) {
      // remove the label from the issues
      issueLabels.forEach((entry) => {
        const issueId = entry.issueId;
        const newLabels = [...entry.labels];
        newLabels.splice(newLabels.indexOf(labelInterface.id), 1);
        modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
      });
    } else {
      // add the label to the issues
      issueLabels.forEach((entry) => {
        const issueId = entry.issueId;
        const newLabels = [...entry.labels];
        // only add the label if it's not already in the list
        if (newLabels.indexOf(labelInterface.id) === -1) {
          newLabels.push(labelInterface.id);
        }
        modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
      });
    }

    // convert modifiedIssueLabels to issueIdToLabelInterfaces using the project labels
    let issueIdToLabelInterfaces: {
      issueId: string;
      labels: LabelInterface[];
    }[] = [];
    modifiedIssueLabels.forEach((entry) => {
      const labels = entry.labels;
      const labelInterfaces: LabelInterface[] = labels
        .map((label) => {
          const labelInterface = (project?.labels as any[])?.find(
            (l) => l.id === label
          );
          if (labelInterface) {
            return labelInterface;
          }
          return null;
        })
        .filter((label) => label !== null);
      issueIdToLabelInterfaces.push({
        issueId: entry.issueId,
        labels: labelInterfaces,
      });
    });

    bulkUpdateIssueLabel(issueIdToLabelInterfaces);
  };

  const changeEstimateOfIssues = (estimate: number | null) => {
    if (selectedIssueIds.length === 0) {
      return;
    }

    bulkUpdateIssueEstimate(selectedIssueIds, estimate);
  };

  const {
    registerCategory,
    showCategory,
    hideCategory,
    open: openCommandPalette,
    setMode,
    setSubMenuConfig,
  } = useCommandPalette();

  const issueSelectionOptions = useMemo<CommandPaletteOption[]>(
    () =>
      [
        {
          icon: <User className="size-4" />,
          name: "Assign to me",
          shortcut: ["I"],
          registerKeys: "i",
          hasFollowUp: false,
          preventDefault: true,
          action: () => {
            assignUserToIssues(userId ?? null);
          },
        },
        {
          icon: <UserPlus className="size-4" />,
          name: "Assign to",
          shortcut: ["A"],
          registerKeys: "a",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Assign to...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const assigneeId = issue?.assignee?.id ?? null;
              return assigneeId === value;
            },
            items: (() => {
              const assignOptions: CommandPaletteSubMenuItem[] = (
                project?.users as any[]
              )?.map((user: any) => ({
                name: user.name,
                value: user.id,
                icon: (
                  <div className="size-4 rounded-md border border-primary-0 overflow-hidden flex-shrink-0 bg-secondary-n-900 flex items-center justify-center">
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-[8px] text-secondary-n-400 font-medium">
                        {user.name
                          ?.split(" ")
                          .map((word) => word[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </span>
                    )}
                  </div>
                ),
              }));

              if (!assignOptions) {
                // users is reliant on API, so we don't want to show anything if it's not loaded yet
                return [];
              }

              assignOptions.sort((a, b) => a.name?.localeCompare(b.name ?? ""));

              assignOptions.unshift({
                name: "Unassigned",
                value: null,
                icon: <UserCircle className="size-4" />,
              });

              return assignOptions;
            })(),
            action: (value) => {
              assignUserToIssues(value);
            },
          },
        },
        {
          icon: <BarChart className="size-4" />,
          name: "Change Priority",
          shortcut: ["P"],
          registerKeys: "p",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Change Priority...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const priorityId = issue?.priority ?? null;
              return priorityId === parseInt(value ?? "0");
            },
            items: (() => {
              const priorityOptions: CommandPaletteSubMenuItem[] =
                defaultPriorities.map((priority) => ({
                  name: priority.name,
                  value: priority.id.toString(),
                  icon: <priority.icon className="size-4" />,
                }));

              priorityOptions.sort(
                (a, b) => parseInt(b.value ?? "0") - parseInt(a.value ?? "0")
              );

              priorityOptions.unshift({
                name: "No Priority",
                value: null,
                icon: <Minus className="size-4" />,
              });

              return priorityOptions;
            })(),
            action: (value) => {
              changePriorityOfIssues(value);
            },
          },
        },
        {
          icon: <CircleDashed className="size-4" />,
          name: "Change Status",
          shortcut: ["S"],
          registerKeys: "s",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Change Status...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              return issue?.status?.name === value;
            },
            items: (() => {
              const statusOptions = columnStatuses.map((status) => ({
                name: status.name,
                value: status.name,
                icon: status.icon(),
              }));

              return statusOptions;
            })(),
            action: (value) => {
              changeStatusOfIssues(value);
            },
          },
        },
        {
          icon: <Calendar className="size-4" />,
          name: "Remove Due Date",
          shortcut: [getModifierKey(), "SHIFT", "D"],
          registerKeys: "mod+shift+d",
          hasFollowUp: false,
          preventDefault: true,
          action: () => {
            setDueDateOfIssues(null);
          },
        },
        {
          icon: <Calendar className="size-4" />,
          name: "Set Due Date",
          shortcut: ["SHIFT", "D"],
          registerKeys: "shift+d",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.DATE_SELECTION,
            searchText: "Set Due Date...",
            items: (() => {
              const today = new Date();
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);
              const inAWeek = new Date(today);
              inAWeek.setDate(inAWeek.getDate() + 7);
              const dateOptions: CommandPaletteSubMenuItem[] = [
                {
                  name: "No Due Date",
                  value: null,
                  icon: <Calendar className="size-4" />,
                },
                {
                  name: "Today",
                  value: today.toISOString(),
                  icon: <Calendar className="size-4" />,
                },
                {
                  name: "Tomorrow",
                  value: tomorrow.toISOString(),
                  icon: <Calendar className="size-4" />,
                },
                {
                  name: "In a Week",
                  value: inAWeek.toISOString(),
                  icon: <Calendar className="size-4" />,
                },
              ];

              return dateOptions;
            })(),
            action: (value) => {
              setDueDateOfIssues(value);
            },
          },
        },
        {
          icon: <Tag className="size-4" />,
          name: "Set Labels",
          shortcut: ["L"],
          registerKeys: "l",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Set Labels...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const labels: string[] = (issue?.labels ??
                []) as unknown as string[];
              if (value === null) {
                return labels.length === 0;
              }
              return labels.some((label) => label === value);
            },
            items: (() => {
              const labelsToDisplay = project?.labels as any[];
              if (!labelsToDisplay) {
                // labels is reliant on API, so we don't want to show anything if it's not loaded yet
                return [];
              }

              const labelOptions: CommandPaletteSubMenuItem[] =
                labelsToDisplay.map((label) => ({
                  name: label.name,
                  value: label.id,
                  icon: (
                    <div
                      className="size-2.5 rounded-[3px]"
                      style={{
                        backgroundColor: label.color?.value || "red",
                      }}
                    />
                  ),
                }));

              labelOptions.sort((a, b) => a.name?.localeCompare(b.name));

              labelOptions.unshift({
                name: "No Label",
                value: null,
                icon: <Tag className="size-4" />,
              });

              return labelOptions;
            })(),
            action: (value) => {
              setLabelOfIssues(value);
            },
          },
        },
        {
          icon: <HalfFilledTriangleIcon className="size-4" />,
          name: "Change Estimate",
          shortcut: ["SHIFT", "E"],
          registerKeys: "shift+e",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Change Estimate...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const estimate = issue?.estimate ?? null;
              const estimateValue = value ? parseInt(value) : null;
              return estimate === estimateValue;
            },
            items: (() => {
              const estimatesToDisplay: (number | null)[] = ESTIMATE_POINTS.map(
                (estimate) => {
                  const tShirtSize = getTShirtSize(estimate);
                  if (!tShirtSize) {
                    return null;
                  }
                  return estimate;
                }
              ).filter((estimate) => estimate !== null);

              const estimateOptions: CommandPaletteSubMenuItem[] =
                estimatesToDisplay.map((estimate) => ({
                  name: getTShirtSize(estimate) ?? "",
                  value: estimate?.toString() ?? "",
                  icon: <HalfFilledTriangleIcon className="size-4" />,
                }));

              estimateOptions.unshift({
                name: "No Estimate",
                value: null,
                icon: <TriangleDashed className="size-4" />,
              });

              return estimateOptions;
            })(),
            action: (value) => {
              changeEstimateOfIssues(value);
            },
          },
        },
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [project?.users, selectedIssueIds]
  );

  useEffect(() => {
    registerCategory(OptionCategory.ISSUE_SELECTION, issueSelectionOptions);
  }, [issueSelectionOptions]);

  useEffect(() => {
    if (selectedIssueIds.length > 0) {
      showCategory(OptionCategory.ISSUE_SELECTION);
      issueSelectionOptions.forEach((option) => {
        if (option.subMenuConfig) {
          registerShortcut(
            option.registerKeys,
            (e) => {
              e.preventDefault();
              openCommandPalette();
              if (option.subMenuConfig) {
                setMode(option.subMenuConfig.mode);
                setSubMenuConfig(option.subMenuConfig);
              }
            },
            { preventDefault: option.preventDefault }
          );
        } else {
          registerShortcut(
            option.registerKeys,
            (e) => {
              e.preventDefault();
              option.action?.();
            },
            { preventDefault: option.preventDefault }
          );
        }
      });
    } else {
      hideCategory(OptionCategory.ISSUE_SELECTION);
      issueSelectionOptions.forEach((option) => {
        unregisterShortcut(option.registerKeys);
      });
    }
  }, [selectedIssueIds]);

  function fetchSelectableIssueIds() {
    const visibleIssueIds = canvasesToShow.map((canvas) => canvas.id);

    let columnIssueIds: string[] = [];
    let targetIssueId: string | null = null;
    if (focusedIssueId) {
      targetIssueId = focusedIssueId;
    } else if (lastFocusedIssueId) {
      targetIssueId = lastFocusedIssueId;
    } else {
      targetIssueId = fetchTopLeftIssueId();
    }

    if (targetIssueId) {
      const focusedCanvas = canvasesToShow.find(
        (canvas) => canvas.id === targetIssueId
      );
      if (focusedCanvas) {
        const focusedPrd = canvasIdToPrd[focusedCanvas.id];
        const focusedStatus = focusedPrd?.status || KanbanDefaultStates.BACKLOG;
        columnIssueIds = sortColumnIssues(
          canvasesToShow
            .filter((canvas) => {
              const prd = canvasIdToPrd[canvas.id];
              return prd?.status === focusedStatus;
            })
            .map((canvas) => canvas.id),
          canvasIdToPrd
        );
      }
    }

    return {
      visibleIssueIds,
      columnIssueIds,
      focusedIssueId,
    };
  }

  function fetchTopLeftIssueId() {
    // Get ordered list of statuses
    const orderedStatuses = Object.values(
      KanbanDefaultStates
    ) as KanbanDefaultStates[];

    // Find first column with issues
    for (const status of orderedStatuses) {
      const columnIssues = sortColumnIssues(
        canvasesToShow.filter((canvas) => {
          const prd = canvasIdToPrd[canvas.id];
          return prd?.status === status;
        }),
        canvasIdToPrd
      );

      if (columnIssues.length > 0) {
        return columnIssues[0].id;
      }
    }
    return null;
  }

  function handleSelectAllShortcut() {
    const { visibleIssueIds } = fetchSelectableIssueIds();

    selectAllVisible(visibleIssueIds);
  }

  function handleSelectAllInColumnShortcut() {
    const { columnIssueIds } = fetchSelectableIssueIds();
    selectAllInColumn(columnIssueIds);
  }

  function handleClearSelectionShortcut() {
    clearSelection();
  }

  function handleToggleSelectionShortcut(issueId: string | null = null) {
    if (issueId) {
      toggleIssueSelection(issueId);
      return;
    }

    const { focusedIssueId } = fetchSelectableIssueIds();

    if (focusedIssueId) {
      toggleIssueSelection(focusedIssueId);
    } else if (lastFocusedIssueId) {
      toggleIssueSelection(lastFocusedIssueId);
    } else {
      const topLeftIssueId = fetchTopLeftIssueId();
      if (topLeftIssueId) {
        toggleIssueSelection(topLeftIssueId);
      }
    }
  }

  // Issue keyboard shortcuts for reordering and status changes
  useIssueKeyboardShortcuts({
    canvasIdToPrd,
    stateToCanvases,
    sortColumnIssues,
    canvasesToShow,
    selectedIssueIds,
    focusedIssueId,
    viewType,
    isEnabled:
      !isLoading && !useProjectModalStore.getState().hasActiveProjectModals(),
  });

  // Handle keyboard navigation and selection
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't handle keyboard navigation while loading or if any project modal is open
      if (
        isLoading ||
        useProjectModalStore.getState().hasActiveProjectModals()
      ) {
        return;
      }

      const { visibleIssueIds, columnIssueIds, focusedIssueId } =
        fetchSelectableIssueIds();

      // Handle selection keyboard shortcuts first
      const isCmd = e.metaKey || e.ctrlKey;
      const isAlt = e.altKey;

      // TODO turn all of these into shortcuts, for now just wrap in a function
      if (isCmd && isAlt && e.code === "KeyA") {
        e.preventDefault();
        handleSelectAllInColumnShortcut();
        return;
      } else if (isCmd && e.code === "KeyA") {
        e.preventDefault();
        handleSelectAllShortcut();
        return;
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClearSelectionShortcut();
        clearFocus();
        return;
      } else if (e.code === "KeyX" && focusedIssueId) {
        e.preventDefault();
        handleToggleSelectionShortcut();
        return;
      }

      // Only handle arrow keys for navigation (non-Alt combinations)
      if (
        !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "j", "k"].includes(
          e.key
        ) ||
        e.altKey // Skip Alt+Arrow combinations as they're handled by mousetrap
      ) {
        return;
      }

      // Don't handle if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      e.preventDefault();

      let currentFocusedId = focusedIssueId;

      // If nothing is focused, check lastFocusedIssueId or find first issue in left-most column
      if (!currentFocusedId) {
        if (canvasesToShow.length === 0) return;

        if (lastFocusedIssueId) {
          // Restore last focused issue if it still exists
          const issueExists = canvasesToShow.some(
            (canvas) => canvas.id === lastFocusedIssueId
          );
          if (issueExists) {
            currentFocusedId = lastFocusedIssueId;
          }
        }

        // If we couldn't restore last focused issue, find first issue in left-most column
        if (!currentFocusedId) {
          currentFocusedId = fetchTopLeftIssueId();
        }

        if (currentFocusedId) {
          setFocusedIssue(currentFocusedId);
        }
      }

      // Find the current issue's position and status
      const currentCanvas = canvasesToShow.find(
        (canvas) => canvas.id === currentFocusedId
      );
      if (!currentCanvas) return;

      const currentPrd = canvasIdToPrd[currentCanvas.id];
      const currentStatus = currentPrd?.status || KanbanDefaultStates.BACKLOG;

      // Get ordered list of statuses
      const orderedStatuses = Object.values(
        KanbanDefaultStates
      ) as KanbanDefaultStates[];
      const currentStatusIndex = orderedStatuses.indexOf(
        currentStatus as KanbanDefaultStates
      );

      // TODO: turn these into shortcuts?
      let nextIndex;
      switch (e.key) {
        case "ArrowUp":
        case "ArrowDown":
        case "j":
        case "k": {
          // Find all issues in the current column
          const currentColumnIssues = sortColumnIssues(
            canvasesToShow.filter((canvas) => {
              const prd = canvasIdToPrd[canvas.id];
              return prd?.status === currentStatus;
            }),
            canvasIdToPrd
          );

          const currentPositionInColumn = currentColumnIssues.findIndex(
            (canvas) => canvas.id === currentFocusedId
          );

          if (e.key === "ArrowUp" || e.key === "k") {
            // Try to move up in current column
            if (currentPositionInColumn > 0) {
              nextIndex = currentColumnIssues[currentPositionInColumn - 1].id;
            } else if (viewType === "list") {
              // Only in list view, look for previous column with issues
              let searchStatusIndex = currentStatusIndex - 1;
              while (searchStatusIndex >= 0) {
                const targetStatus = orderedStatuses[searchStatusIndex];
                const targetColumnIssues = sortColumnIssues(
                  canvasesToShow.filter((canvas) => {
                    const prd = canvasIdToPrd[canvas.id];
                    return prd?.status === targetStatus;
                  }),
                  canvasIdToPrd
                );

                if (targetColumnIssues.length > 0) {
                  // Move to last issue in previous column with issues
                  nextIndex =
                    targetColumnIssues[targetColumnIssues.length - 1].id;
                  break;
                }
                searchStatusIndex--;
              }
            }
          } else {
            // Try to move down in current column
            if (currentPositionInColumn < currentColumnIssues.length - 1) {
              nextIndex = currentColumnIssues[currentPositionInColumn + 1].id;
            } else if (viewType === "list") {
              // Only in list view, look for next column with issues
              let searchStatusIndex = currentStatusIndex + 1;
              while (searchStatusIndex < orderedStatuses.length) {
                const targetStatus = orderedStatuses[searchStatusIndex];
                const targetColumnIssues = sortColumnIssues(
                  canvasesToShow.filter((canvas) => {
                    const prd = canvasIdToPrd[canvas.id];
                    return prd?.status === targetStatus;
                  }),
                  canvasIdToPrd
                );

                if (targetColumnIssues.length > 0) {
                  // Move to first issue in next column with issues
                  nextIndex = targetColumnIssues[0].id;
                  break;
                }
                searchStatusIndex++;
              }
            }
          }
          break;
        }

        case "ArrowLeft":
        case "ArrowRight": {
          // Get the target status
          let searchStatusIndex = currentStatusIndex;
          const direction = e.key === "ArrowLeft" ? -1 : 1;

          // Find current position in column
          const currentColumnIssues = sortColumnIssues(
            canvasesToShow.filter((canvas) => {
              const prd = canvasIdToPrd[canvas.id];
              return prd?.status === currentStatus;
            }),
            canvasIdToPrd
          );
          const currentPositionInColumn = currentColumnIssues.findIndex(
            (canvas) => canvas.id === currentFocusedId
          );

          // Keep searching in the direction until we find a column with issues or hit the edge
          while (true) {
            searchStatusIndex += direction;

            // Stop if we've hit the edge
            if (
              searchStatusIndex < 0 ||
              searchStatusIndex >= orderedStatuses.length
            ) {
              break;
            }

            const targetStatus = orderedStatuses[searchStatusIndex];

            // Get all issues in the target column
            const targetColumnIssues = sortColumnIssues(
              canvasesToShow.filter((canvas) => {
                const prd = canvasIdToPrd[canvas.id];
                return prd?.status === targetStatus;
              }),
              canvasIdToPrd
            );

            // If we found a column with issues
            if (targetColumnIssues.length > 0) {
              if (viewType === "list") {
                // In list view, always focus the first issue in the target column
                nextIndex = targetColumnIssues[0].id;
              } else {
                // In grid view, try to maintain the same index position
                if (currentPositionInColumn < targetColumnIssues.length) {
                  nextIndex = targetColumnIssues[currentPositionInColumn].id;
                } else {
                  // If target column is shorter, focus the last issue
                  nextIndex =
                    targetColumnIssues[targetColumnIssues.length - 1].id;
                }
              }
              break;
            }
            // If this column was empty, the while loop will continue to the next column
          }
          break;
        }
      }

      // Update focus if we found a valid next issue
      if (nextIndex) {
        setFocusedIssue(nextIndex);
      }
    },
    [
      canvasesToShow,
      focusedIssueId,
      lastFocusedIssueId,
      canvasIdToPrd,
      sortColumnIssues,
      viewType,
      isLoading,
      selectedIssueIds,
      setFocusedIssue,
      setLastFocusedIssue,
      clearFocus,
    ]
  );

  // Add keyboard event listener
  useEffect(() => {
    // Only add keyboard listener if not loading
    if (!isLoading) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [handleKeyDown, isLoading]);

  const handleGlobalClick = useCallback(
    (e: MouseEvent) => {
      // Don't handle clicks while loading
      if (isLoading) return;

      // Don't clear focus if clicking inside an issue
      const isClickInsideIssue = Object.values(issueRefs.current).some((ref) =>
        ref.contains(e.target as Node)
      );

      if (!isClickInsideIssue) {
        clearFocus();
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          clearSelection();
        }
      }
    },
    [isLoading, clearSelection]
  );

  // Add global click listener
  useEffect(() => {
    if (!isLoading) {
      window.addEventListener("click", handleGlobalClick);
      return () => window.removeEventListener("click", handleGlobalClick);
    }
  }, [handleGlobalClick, isLoading]);

  // Create refs for the container and to track focused elements
  const containerRef = useRef<HTMLDivElement>(null);
  const issueRefs = useRef<{ [key: string]: HTMLDivElement }>({});

  // Scroll focused issue into view when focus changes
  useEffect(() => {
    if (focusedIssueId && issueRefs.current[focusedIssueId]) {
      issueRefs.current[focusedIssueId].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [focusedIssueId]);

  const handleItemClick = useCallback(
    (project: Project, canvas: any) => {
      const e = window.event as MouseEvent;

      // Always update focus
      setLastFocusedIssue(canvas.id);

      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();

        handleToggleSelectionShortcut(canvas.id);
      } else if (e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();

        if (selectedIssueIds.length === 0) {
          // if there are no selected issues, select the current issue
          handleToggleSelectionShortcut(canvas.id);
          setFocusedIssue(canvas.id);
        } else {
          const lastSelectedIssue = canvasesToShow.find(
            (canvas) => canvas.id === lastSelectedIssueId
          );

          if (!lastSelectedIssue) return;

          clearSelection();

          const lastSelectedIssueStatus =
            canvasIdToPrd[lastSelectedIssue.id]?.status;
          const lastSelectedIssueInfo = {
            status: lastSelectedIssueStatus,
            columnIndex: Object.values(KanbanDefaultStates).indexOf(
              lastSelectedIssueStatus as KanbanDefaultStates
            ),
            indexInColumn: sortColumnIssues(
              canvasesToShow.filter((c) => {
                const prd = canvasIdToPrd[c.id];
                return prd?.status === lastSelectedIssueStatus;
              }),
              canvasIdToPrd
            ).findIndex((c) => c.id === lastSelectedIssue.id),
          };
          const currentIssueStatus = canvasIdToPrd[canvas.id]?.status;
          const currentIssueInfo = {
            status: canvasIdToPrd[canvas.id]?.status,
            columnIndex: Object.values(KanbanDefaultStates).indexOf(
              canvasIdToPrd[canvas.id]?.status as KanbanDefaultStates
            ),
            indexInColumn: sortColumnIssues(
              canvasesToShow.filter((c) => {
                const prd = canvasIdToPrd[c.id];
                return prd?.status === currentIssueStatus;
              }),
              canvasIdToPrd
            ).findIndex((c) => c.id === canvas.id),
          };

          let selectableIssueIds: string[] = [];
          let selectedIssueId: string = "";

          if (lastSelectedIssueId === canvas.id) {
            // if the last selected issue is the current issue, just select the current issue
            selectedIssueId = canvas.id;
            selectableIssueIds = [canvas.id];
          } else if (
            lastSelectedIssueInfo.columnIndex === currentIssueInfo.columnIndex
          ) {
            // the issues are in the same column, select issues in between
            const startIndex = Math.min(
              lastSelectedIssueInfo.indexInColumn,
              currentIssueInfo.indexInColumn
            );
            const endIndex = Math.max(
              lastSelectedIssueInfo.indexInColumn,
              currentIssueInfo.indexInColumn
            );

            const columnIssues = sortColumnIssues(
              canvasesToShow.filter((c) => {
                const prd = canvasIdToPrd[c.id];
                return prd?.status === lastSelectedIssueStatus;
              }),
              canvasIdToPrd
            );
            const slicedColumnIssues = columnIssues.slice(
              startIndex,
              endIndex + 1
            );
            selectableIssueIds = slicedColumnIssues.map((c) => c.id);
            selectedIssueId = lastSelectedIssueId ?? "";
          } else {
            // the issues are in different columns, select a range of issues
            const lowerIssueInfo =
              lastSelectedIssueInfo.columnIndex < currentIssueInfo.columnIndex
                ? lastSelectedIssueInfo
                : currentIssueInfo;
            const higherIssueInfo =
              lastSelectedIssueInfo.columnIndex < currentIssueInfo.columnIndex
                ? currentIssueInfo
                : lastSelectedIssueInfo;

            const kanbanStates = Object.values(KanbanDefaultStates);
            for (
              let i = lowerIssueInfo.columnIndex;
              i <= higherIssueInfo.columnIndex;
              i++
            ) {
              const columnIssues = sortColumnIssues(
                canvasesToShow.filter((c) => {
                  const prd = canvasIdToPrd[c.id];
                  return prd?.status === kanbanStates[i];
                }),
                canvasIdToPrd
              );

              let issuesToAdd: any[] = [];
              if (i === lowerIssueInfo.columnIndex) {
                // add the lower column issue, and issues after it
                issuesToAdd = columnIssues.slice(
                  lowerIssueInfo.indexInColumn,
                  columnIssues.length
                );
              } else if (i === higherIssueInfo.columnIndex) {
                // add the higher column issue, and issues before it
                issuesToAdd = columnIssues.slice(
                  0,
                  higherIssueInfo.indexInColumn + 1
                );
              } else {
                // add all issues in the column
                issuesToAdd = columnIssues;
              }
              selectableIssueIds.push(...issuesToAdd.map((c) => c.id));
            }

            selectedIssueId = lastSelectedIssueId ?? "";
          }
          selectAllVisible(selectableIssueIds);
          setFocusedIssue(selectedIssueId);
        }
      } else {
        onSelect(project, canvas);
      }
    },
    [
      onSelect,
      project,
      handleToggleSelectionShortcut,
      clearSelection,
      selectedIssueIds,
      lastSelectedIssueId,
      canvasesToShow,
      canvasIdToPrd,
      sortColumnIssues,
    ]
  );

  return (
    <div
      className="flex flex-1 flex-col h-full overflow-x-auto overflow-y-hidden select-none"
      ref={containerRef}
    >
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="text-muted-foreground animate-spin" />
            <p className="text-sm text-muted-foreground">Loading canvases...</p>
          </div>
        </div>
      ) : viewType === "list" ? (
        <div
          className="h-[calc(100vh-120px)] overflow-y-auto"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgb(82 82 91) transparent",
          }}
        >
          <div className="flex flex-col divide-y bg-secondary-n-1000">
            {Object.keys(KanbanDefaultStates).map((key) => {
              const status = KanbanDefaultStates[key];
              const statusId = STATE_KEY_TO_STATUS_ID[key] || "backlog";
              const statusObj = STATUS_LIST.find((s) => s.id === statusId);
              const StatusIcon = statusObj?.icon;
              const StatusHeaderIcon = statusObj?.headerIcon;

              // Get and sort issues for this status
              const statusCanvases = sortColumnIssues(
                canvasesToShow.filter((canvas) => {
                  const prd = canvasIdToPrd[canvas.id];
                  return (
                    (prd?.status ?? KanbanDefaultStates.BACKLOG) === status
                  );
                }),
                canvasIdToPrd
              );

              return (
                <div key={key} className="flex flex-col flex-shrink-0">
                  {/* Status Header */}
                  <div className="flex items-center justify-between pr-9 pl-11 py-2.5 bg-secondary-n-950 sticky top-0">
                    <div className="flex items-center gap-2">
                      {StatusHeaderIcon && <StatusHeaderIcon />}
                      <span className="caption text-2xs font-medium">
                        {status}
                      </span>
                      <span className="px-1 rounded-xs text-2xs font-medium bg-secondary-n-900 text-center flex items-center justify-center">
                        {statusCanvases.length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleButtonClick(key)}
                        className="flex items-center justify-center w-7 h-7 rounded-xs hover:bg-white/10 transition-colors text-lg text-secondary-n-600"
                        aria-label="Add Feature"
                        type="button"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Status Items */}
                  {statusCanvases.map((canvas) => {
                    const issue = mapCanvasToIssue(
                      canvas,
                      canvasIdToPrd[canvas.id],
                      Array.isArray(project?.labels) ? project.labels : [],
                      Array.isArray(project?.users) ? project.users : []
                    );

                    return (
                      <IssueList
                        key={canvas.id}
                        issue={issue}
                        project={project as Project}
                        canvas={canvas}
                        statuses={columnStatuses}
                        onSelect={() =>
                          handleItemClick(project as Project, canvas)
                        }
                        onAssignUser={updateCanvasAssignedUserId}
                        icon={StatusIcon}
                        issueRefs={issueRefs.current}
                        visibleIssueIds={canvasesToShow.map((c) => c.id)}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <DragDropContext
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          enableDefaultSensors={true}
        >
          <div className="flex flex-row min-h-[calc(100vh-120px)] relative px-1 overflow-x-auto">
            {Object.keys(KanbanDefaultStates).map((key) => {
              const colState = stateToCanvases[KanbanDefaultStates[key]];
              return (
                <KanbanColumn
                  key={key}
                  stateKey={colState.stateKey}
                  title={colState.title}
                  canvases={colState.canvases}
                  project={project}
                  canvasIdToPrd={canvasIdToPrd}
                  onSelect={handleItemClick}
                  isDragging={isDragging}
                  onClickAdd={() => handleButtonClick(key)}
                  updateCanvasLabels={updateCanvasLabels}
                  updateProjectLabels={updateProjectLabels}
                  updateCanvasAssignedUserId={updateCanvasAssignedUserId}
                  showCreateRepo={canvasesToShow.length === 1 && !hasRepo}
                  issueRefs={issueRefs.current}
                  visibleIssueIds={canvasesToShow.map((c) => c.id)}
                />
              );
            })}
          </div>
        </DragDropContext>
      )}
      <CreateCanvasModal
        openCanvasState={isOpen ? KanbanDefaultStates[isOpen] : isOpen}
        setOpenCanvasState={(state: string | null) => {
          setIsOpen(null);
          useProjectModalStore
            .getState()
            .removeProjectModal("create-canvas-modal");
        }}
        project={project}
        createIndex={
          typeof isOpen === "string" && KanbanDefaultStates[isOpen]
            ? stateToCanvases?.[KanbanDefaultStates[isOpen]]?.canvases
                ?.length || 0
            : 0
        }
      />
      <NoRepositoryModal
        isOpen={showNoRepoModal}
        onClose={() => setShowNoRepoModal(false)}
        onCreateRepository={handleCreateRepository}
      />

      {showCreateRepoModal && canvasesToShow && canvasesToShow.length > 0 && (
        <CreateRepositoryModal
          canvasId={canvasesToShow[0].id}
          isOpen={showCreateRepoModal}
          onClose={() => {
            setShowCreateRepoModal(false);
          }}
          onRepoCreated={() => {
            setShowCreateRepoModal(false);
          }}
          initialSelectedOrg={null}
          originatingProvider={originatingGitProvider}
        />
      )}
    </div>
  );
};

export default React.memo(CanvasKanban);
